import React, { useState, useEffect } from 'react';
import './NotificationPrompt.css';

const DISMISS_KEY = 'homemanager_notif_prompt_dismissed';
const DISMISS_IOS_INSTALL_KEY = 'homemanager_ios_install_prompt_dismissed';

export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Soft prompt after login.
 * On iOS Safari (browser tab), push APIs are unavailable until the app is
 * added to the Home Screen — so we guide that install first.
 */
const NotificationPrompt = ({ enabled, onEnable }) => {
  const [mode, setMode] = useState(null); // 'enable' | 'ios-install' | 'ios-update' | null
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (enabled) {
      setMode(null);
      return;
    }

    const ios = isIosDevice();
    const standalone = isStandalonePwa();
    const pushOk = isPushSupported();

    try {
      if (pushOk && localStorage.getItem(DISMISS_KEY) === '1') {
        return;
      }
      if (ios && !standalone && localStorage.getItem(DISMISS_IOS_INSTALL_KEY) === '1') {
        return;
      }
    } catch (_) {
      /* ignore */
    }

    let next = null;
    if (pushOk) {
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        return;
      }
      next = 'enable';
    } else if (ios && !standalone) {
      // Safari tab: push only works from Home Screen PWA (iOS 16.4+)
      next = 'ios-install';
    } else if (ios && standalone && !pushOk) {
      next = 'ios-update';
    } else {
      return;
    }

    const timer = setTimeout(() => setMode(next), 1500);
    return () => clearTimeout(timer);
  }, [enabled]);

  if (!mode) return null;

  const dismiss = () => {
    try {
      if (mode === 'enable') {
        localStorage.setItem(DISMISS_KEY, '1');
      } else if (mode === 'ios-install') {
        localStorage.setItem(DISMISS_IOS_INSTALL_KEY, '1');
      }
    } catch (_) {
      /* ignore */
    }
    setMode(null);
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      await onEnable();
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch (_) {
        /* ignore */
      }
      setMode(null);
    } catch (error) {
      console.error('Enable notifications failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'ios-install') {
    return (
      <div className="notif-prompt-overlay" role="dialog" aria-labelledby="notif-prompt-title">
        <div className="notif-prompt-card">
          <div className="notif-prompt-icon" aria-hidden="true">📱</div>
          <h2 id="notif-prompt-title">Enable notifications on iPhone</h2>
          <p>
            On iOS, push notifications only work after HomeManager is added to your Home Screen.
          </p>
          <ol className="notif-prompt-steps">
            <li>
              Tap the <strong>Share</strong> button
              <span className="notif-prompt-share" aria-hidden="true"> ⎋ </span>
              in Safari
            </li>
            <li>
              Choose <strong>Add to Home Screen</strong>
            </li>
            <li>
              Open HomeManager from the icon, then allow notifications
            </li>
          </ol>
          <div className="notif-prompt-actions">
            <button type="button" className="notif-prompt-enable" onClick={dismiss}>
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'ios-update') {
    return (
      <div className="notif-prompt-overlay" role="dialog" aria-labelledby="notif-prompt-title">
        <div className="notif-prompt-card">
          <div className="notif-prompt-icon" aria-hidden="true">🔔</div>
          <h2 id="notif-prompt-title">Update iOS for notifications</h2>
          <p>
            Push notifications need iOS 16.4 or later. Update your device, then reopen
            HomeManager from the Home Screen icon.
          </p>
          <div className="notif-prompt-actions">
            <button type="button" className="notif-prompt-enable" onClick={dismiss}>
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notif-prompt-overlay" role="dialog" aria-labelledby="notif-prompt-title">
      <div className="notif-prompt-card">
        <div className="notif-prompt-icon" aria-hidden="true">🔔</div>
        <h2 id="notif-prompt-title">Stay updated</h2>
        <p>
          Allow push notifications to know when groceries or stock items are added —
          even when the app is closed.
        </p>
        <div className="notif-prompt-actions">
          <button type="button" className="notif-prompt-later" onClick={dismiss} disabled={loading}>
            Not now
          </button>
          <button type="button" className="notif-prompt-enable" onClick={handleEnable} disabled={loading}>
            {loading ? 'Enabling…' : 'Enable notifications'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
